import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { LayoutDashboard, Package, Users, Instagram, LogOut, Plus, Pencil, Trash2, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUploader from "@/components/admin/ImageUploader";

const CATS = ["fight-shorts", "rash-guards", "t-shirts", "training-shirts", "hoodies", "compression", "womens", "accessories"];
const GENDERS = ["unisex", "men", "women"];

const lines2arr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arr2lines = (a) => (a || []).join("\n");
const colors2str = (a) => (a || []).map((c) => `${c.name} ${c.hex}`).join("\n");
const str2colors = (s) => lines2arr(s).map((l) => { const i = l.lastIndexOf(" "); return { name: l.slice(0, i).trim() || l, hex: l.slice(i + 1).trim() || "#888888" }; });
const csv2arr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [fighters, setFighters] = useState([]);
  const [instagram, setInstagram] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [editFighter, setEditFighter] = useState(null);

  const logout = useCallback(() => { localStorage.removeItem("vorna_token"); navigate("/admin/login"); }, [navigate]);
  const loadAll = useCallback(async () => {
    const [s, p, f, ig] = await Promise.all([
      api.get("/admin/stats"), api.get("/products"), api.get("/fighters"), api.get("/instagram"),
    ]);
    setStats(s.data); setProducts(p.data); setFighters(f.data); setInstagram(ig.data);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("vorna_token")) { navigate("/admin/login"); return; }
    api.get("/auth/me").then(() => { setAuthed(true); loadAll(); }).catch(() => logout());
  }, [navigate, logout, loadAll]);

  if (!authed) return <div className="min-h-screen bg-vorna-bg flex items-center justify-center text-white/40 font-display uppercase tracking-widest">Cargando…</div>;

  const NAV = [
    { k: "overview", l: "Resumen", icon: <LayoutDashboard className="h-4 w-4" /> },
    { k: "products", l: "Productos", icon: <Package className="h-4 w-4" /> },
    { k: "fighters", l: "Atletas", icon: <Users className="h-4 w-4" /> },
    { k: "instagram", l: "Instagram", icon: <Instagram className="h-4 w-4" /> },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-vorna-bg text-white flex">
      <aside className="w-16 lg:w-60 border-r border-white/10 flex flex-col shrink-0">
        <div className="h-16 flex items-center justify-center lg:justify-start lg:px-6 border-b border-white/10"><Logo imgClass="h-8" to="/admin" /></div>
        <nav className="flex-1 p-2 lg:p-4 space-y-1">
          {NAV.map((n) => (
            <button key={n.k} data-testid={`admin-tab-${n.k}`} onClick={() => setTab(n.k)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm tracking-wide transition-colors ${tab === n.k ? "bg-vorna-accent text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              {n.icon}<span className="hidden lg:inline">{n.l}</span>
            </button>
          ))}
        </nav>
        <button data-testid="admin-logout" onClick={logout} className="m-2 lg:m-4 flex items-center gap-3 px-3 py-2.5 text-sm text-white/60 hover:text-vorna-accent"><LogOut className="h-4 w-4" /><span className="hidden lg:inline">Salir</span></button>
      </aside>

      <div className="flex-1 overflow-y-auto">
        <div className="p-5 lg:p-10 max-w-6xl">
          <h1 className="font-display uppercase tracking-tighter text-3xl lg:text-5xl mb-8">{NAV.find((n) => n.k === tab)?.l}</h1>

          {tab === "overview" && stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={<Package />} label="Productos" value={stats.products} />
              <StatCard icon={<Star />} label="Destacados" value={stats.featured} />
              <StatCard icon={<Users />} label="Atletas" value={stats.fighters} />
              <StatCard icon={<Instagram />} label="Posts IG" value={stats.instagram} />
            </div>
          )}

          {tab === "products" && (
            <div>
              <Button data-testid="add-product-button" onClick={() => setEditProduct({})} className="mb-6 rounded-none bg-vorna-accent hover:bg-vorna-accentHover"><Plus className="h-4 w-4 mr-2" /> Añadir producto</Button>
              <div className="border border-white/10 divide-y divide-white/5">
                {products.map((p) => (
                  <div key={p.id} data-testid={`admin-product-${p.slug}`} className="flex items-center gap-4 p-4">
                    {p.images?.[0] ? <img src={p.images[0]} alt="" className="h-14 w-12 object-cover bg-vorna-elevated" /> : <div className="h-14 w-12 bg-vorna-elevated" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{p.name}</p>
                      <p className="text-xs text-white/40 uppercase tracking-widest">{p.category} · {p.price ? `$${p.price}` : "Bajo consulta"} {p.featured && "· Destacado"}</p>
                    </div>
                    <button onClick={() => setEditProduct(p)} data-testid={`edit-product-${p.slug}`} className="p-2 hover:text-vorna-accent"><Pencil className="h-4 w-4" /></button>
                    <button onClick={async () => { if (window.confirm("¿Eliminar producto?")) { await api.delete(`/admin/products/${p.id}`); toast.success("Eliminado"); loadAll(); } }} className="p-2 hover:text-vorna-accent"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "fighters" && (
            <div>
              <Button data-testid="add-fighter-button" onClick={() => setEditFighter({})} className="mb-6 rounded-none bg-vorna-accent hover:bg-vorna-accentHover"><Plus className="h-4 w-4 mr-2" /> Añadir atleta</Button>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {fighters.map((f) => (
                  <div key={f.id} data-testid={`admin-fighter-${f.id}`} className="border border-white/10 p-4 flex gap-4">
                    {f.image ? <img src={f.image} alt="" className="h-20 w-16 object-cover bg-vorna-elevated" /> : <div className="h-20 w-16 bg-vorna-elevated flex items-center justify-center text-white/20"><Users className="h-6 w-6" /></div>}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{f.name}</p>
                      <p className="text-xs text-vorna-accent">"{f.nickname}"</p>
                      <p className="text-xs text-white/40 mt-1">{f.record}</p>
                      <div className="mt-2 flex gap-2">
                        <button onClick={() => setEditFighter(f)} className="p-1.5 hover:text-vorna-accent"><Pencil className="h-4 w-4" /></button>
                        <button onClick={async () => { if (window.confirm("¿Eliminar atleta?")) { await api.delete(`/admin/fighters/${f.id}`); toast.success("Eliminado"); loadAll(); } }} className="p-1.5 hover:text-vorna-accent"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "instagram" && <InstagramPanel posts={instagram} onChange={loadAll} />}
        </div>
      </div>

      {editProduct && <ProductDialog product={editProduct} onClose={() => setEditProduct(null)} onSaved={() => { setEditProduct(null); loadAll(); }} />}
      {editFighter && <FighterDialog fighter={editFighter} onClose={() => setEditFighter(null)} onSaved={() => { setEditFighter(null); loadAll(); }} />}
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="border border-white/10 p-6">
      <div className="text-vorna-accent mb-4">{icon}</div>
      <p className="font-display text-3xl">{value}</p>
      <p className="text-xs text-white/40 tracking-widest uppercase mt-1">{label}</p>
    </div>
  );
}

function L({ label, children }) {
  return <div><label className="text-[11px] tracking-widest text-white/40 uppercase mb-2 block">{label}</label>{children}</div>;
}
const inputCls = "rounded-none border-white/15 bg-transparent";

function InstagramPanel({ posts, onChange }) {
  const [image, setImage] = useState("");
  const [caption, setCaption] = useState("");
  const [link, setLink] = useState("https://instagram.com/vorna");
  const add = async () => {
    if (!image) { toast.error("Sube una imagen primero"); return; }
    await api.post("/admin/instagram", { image, caption, link, order: posts.length });
    toast.success("Post añadido"); setImage(""); setCaption(""); onChange();
  };
  return (
    <div>
      <div className="border border-white/10 p-5 mb-6 space-y-4 max-w-xl">
        <p className="font-display uppercase tracking-widest text-sm">Añadir post</p>
        <ImageUploader label="Subir imagen" testid="ig-image-uploader" onUpload={(url) => setImage(url)} />
        {image && <img src={image} alt="" className="h-28 w-28 object-cover border border-white/10" />}
        <L label="Descripción"><Input data-testid="ig-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} className={inputCls} /></L>
        <L label="Enlace (URL del post)"><Input value={link} onChange={(e) => setLink(e.target.value)} className={`${inputCls} font-mono text-xs`} /></L>
        <Button data-testid="add-ig-button" onClick={add} className="rounded-none bg-vorna-accent hover:bg-vorna-accentHover"><Plus className="h-4 w-4 mr-2" /> Añadir al feed</Button>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {posts.map((p) => (
          <div key={p.id} data-testid={`admin-ig-${p.id}`} className="group relative aspect-square overflow-hidden bg-vorna-elevated border border-white/10">
            <img src={p.image} alt="" className="h-full w-full object-cover" />
            <button onClick={async () => { await api.delete(`/admin/instagram/${p.id}`); toast.success("Eliminado"); onChange(); }} data-testid={`delete-ig-${p.id}`}
              className="absolute top-2 right-2 h-8 w-8 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity hover:text-vorna-accent"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductDialog({ product, onClose, onSaved }) {
  const isNew = !product.id;
  const [f, setF] = useState({
    name: product.name || "", slug: product.slug || "", category: product.category || "fight-shorts",
    gender: product.gender || "unisex", disciplines: (product.disciplines || []).join(", "),
    price: product.price ?? "", description: product.description || "", description_en: product.description_en || "",
    material: product.material || "", material_en: product.material_en || "",
    care: product.care || "", care_en: product.care_en || "",
    intended_use: product.intended_use || "", intended_use_en: product.intended_use_en || "",
    features: arr2lines(product.features), features_en: arr2lines(product.features_en),
    images: arr2lines(product.images), colors: colors2str(product.colors),
    sizes: (product.sizes || []).join(", "), featured: !!product.featured,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name) { toast.error("Nombre requerido"); return; }
    const slug = (f.slug || f.name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const payload = {
      name: f.name, slug, category: f.category, gender: f.gender, disciplines: csv2arr(f.disciplines),
      price: f.price === "" ? null : Number(f.price),
      description: f.description, description_en: f.description_en,
      material: f.material, material_en: f.material_en, care: f.care, care_en: f.care_en,
      intended_use: f.intended_use, intended_use_en: f.intended_use_en,
      features: lines2arr(f.features), features_en: lines2arr(f.features_en),
      images: lines2arr(f.images), colors: str2colors(f.colors),
      sizes: csv2arr(f.sizes), featured: f.featured,
    };
    try {
      if (isNew) await api.post("/admin/products", payload); else await api.put(`/admin/products/${product.id}`, payload);
      toast.success("Guardado"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none bg-vorna-surface border-white/15 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight text-2xl">{isNew ? "Añadir producto" : "Editar producto"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <L label="Nombre"><Input data-testid="product-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></L>
            <L label="Precio (USD) — vacío = bajo consulta"><Input data-testid="product-price-input" type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className={inputCls} /></L>
            <L label="Categoría"><Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger className={inputCls}><SelectValue /></SelectTrigger><SelectContent className="rounded-none bg-vorna-elevated border-white/15 text-white">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></L>
            <L label="Género"><Select value={f.gender} onValueChange={(v) => set("gender", v)}><SelectTrigger className={inputCls}><SelectValue /></SelectTrigger><SelectContent className="rounded-none bg-vorna-elevated border-white/15 text-white">{GENDERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></L>
            <L label="Disciplinas (mma, bjj, grappling, training)"><Input value={f.disciplines} onChange={(e) => set("disciplines", e.target.value)} className={inputCls} /></L>
            <L label="Tallas (separadas por coma)"><Input value={f.sizes} onChange={(e) => set("sizes", e.target.value)} placeholder="S, M, L, XL" className={inputCls} /></L>
          </div>
          <L label="Descripción (ES)"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></L>
          <L label="Description (EN)"><Textarea value={f.description_en} onChange={(e) => set("description_en", e.target.value)} className={inputCls} /></L>
          <L label="Fotos (una URL por línea)"><Textarea value={f.images} onChange={(e) => set("images", e.target.value)} className={`${inputCls} font-mono text-xs`} /></L>
          <ImageUploader label="Subir fotos del producto" testid="product-image-uploader" onUpload={(url) => setF((p) => ({ ...p, images: p.images ? p.images + "\n" + url : url }))} />
          <L label="Colores (uno por línea: Nombre #hex)"><Textarea value={f.colors} onChange={(e) => set("colors", e.target.value)} placeholder="Negro #0A0A0A" className={`${inputCls} font-mono text-xs`} /></L>
          <div className="grid sm:grid-cols-2 gap-4">
            <L label="Material (ES)"><Input value={f.material} onChange={(e) => set("material", e.target.value)} className={inputCls} /></L>
            <L label="Material (EN)"><Input value={f.material_en} onChange={(e) => set("material_en", e.target.value)} className={inputCls} /></L>
            <L label="Uso recomendado (ES)"><Input value={f.intended_use} onChange={(e) => set("intended_use", e.target.value)} className={inputCls} /></L>
            <L label="Intended use (EN)"><Input value={f.intended_use_en} onChange={(e) => set("intended_use_en", e.target.value)} className={inputCls} /></L>
            <L label="Cuidados (ES)"><Input value={f.care} onChange={(e) => set("care", e.target.value)} className={inputCls} /></L>
            <L label="Care (EN)"><Input value={f.care_en} onChange={(e) => set("care_en", e.target.value)} className={inputCls} /></L>
          </div>
          <L label="Características ES (una por línea)"><Textarea value={f.features} onChange={(e) => set("features", e.target.value)} className={inputCls} /></L>
          <L label="Features EN (una por línea)"><Textarea value={f.features_en} onChange={(e) => set("features_en", e.target.value)} className={inputCls} /></L>
          <label className="flex items-center gap-3 text-sm"><Switch checked={f.featured} onCheckedChange={(v) => set("featured", v)} data-testid="product-featured-switch" /> Destacado</label>
        </div>
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancelar</Button>
          <Button data-testid="save-product-button" onClick={save} className="rounded-none bg-vorna-accent hover:bg-vorna-accentHover">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FighterDialog({ fighter, onClose, onSaved }) {
  const isNew = !fighter.id;
  const [f, setF] = useState({
    id: fighter.id || "", name: fighter.name || "", nickname: fighter.nickname || "", record: fighter.record || "",
    weight_class: fighter.weight_class || "", discipline: fighter.discipline || "", gym: fighter.gym || "",
    city: fighter.city || "", country: fighter.country || "Ecuador", instagram: fighter.instagram || "",
    bio: fighter.bio || "", bio_en: fighter.bio_en || "",
    achievements: arr2lines(fighter.achievements), achievements_en: arr2lines(fighter.achievements_en),
    image: fighter.image || "", action_images: arr2lines(fighter.action_images), products: (fighter.products || []).join(", "),
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name) { toast.error("Nombre requerido"); return; }
    const payload = {
      name: f.name, nickname: f.nickname, record: f.record, weight_class: f.weight_class, discipline: f.discipline,
      gym: f.gym, city: f.city, country: f.country, instagram: f.instagram,
      bio: f.bio, bio_en: f.bio_en, achievements: lines2arr(f.achievements), achievements_en: lines2arr(f.achievements_en),
      image: f.image, action_images: lines2arr(f.action_images), products: csv2arr(f.products),
    };
    if (isNew && f.id) payload.id = f.id.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    try {
      if (isNew) await api.post("/admin/fighters", payload); else await api.put(`/admin/fighters/${fighter.id}`, payload);
      toast.success("Guardado"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none bg-vorna-surface border-white/15 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight text-2xl">{isNew ? "Añadir atleta" : "Editar atleta"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <L label="Nombre"><Input data-testid="fighter-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></L>
            <L label="Apodo"><Input value={f.nickname} onChange={(e) => set("nickname", e.target.value)} className={inputCls} /></L>
            <L label="Récord"><Input value={f.record} onChange={(e) => set("record", e.target.value)} placeholder="14-3-0" className={inputCls} /></L>
            <L label="Categoría de peso"><Input value={f.weight_class} onChange={(e) => set("weight_class", e.target.value)} className={inputCls} /></L>
            <L label="Disciplina"><Input value={f.discipline} onChange={(e) => set("discipline", e.target.value)} placeholder="MMA" className={inputCls} /></L>
            <L label="Equipo / Academia"><Input value={f.gym} onChange={(e) => set("gym", e.target.value)} className={inputCls} /></L>
            <L label="Ciudad"><Input value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></L>
            <L label="País"><Input value={f.country} onChange={(e) => set("country", e.target.value)} className={inputCls} /></L>
            <L label="Instagram URL"><Input value={f.instagram} onChange={(e) => set("instagram", e.target.value)} className={`${inputCls} font-mono text-xs`} /></L>
          </div>
          {isNew && <L label="ID (slug opcional)"><Input value={f.id} onChange={(e) => set("id", e.target.value)} placeholder="auto si vacío" className={inputCls} /></L>}
          <L label="Biografía (ES)"><Textarea value={f.bio} onChange={(e) => set("bio", e.target.value)} className={inputCls} /></L>
          <L label="Biography (EN)"><Textarea value={f.bio_en} onChange={(e) => set("bio_en", e.target.value)} className={inputCls} /></L>
          <L label="Logros ES (uno por línea)"><Textarea value={f.achievements} onChange={(e) => set("achievements", e.target.value)} className={inputCls} /></L>
          <L label="Achievements EN (one per line)"><Textarea value={f.achievements_en} onChange={(e) => set("achievements_en", e.target.value)} className={inputCls} /></L>
          <L label="Foto principal (URL)"><Input value={f.image} onChange={(e) => set("image", e.target.value)} className={`${inputCls} font-mono text-xs`} /></L>
          <ImageUploader label="Subir foto principal" testid="fighter-image-uploader" onUpload={(url) => set("image", url)} />
          <L label="Fotos de acción (una URL por línea)"><Textarea value={f.action_images} onChange={(e) => set("action_images", e.target.value)} className={`${inputCls} font-mono text-xs`} /></L>
          <ImageUploader label="Subir fotos de acción" testid="fighter-action-uploader" onUpload={(url) => setF((p) => ({ ...p, action_images: p.action_images ? p.action_images + "\n" + url : url }))} />
          <L label="IDs de productos que usa (separados por coma)"><Input value={f.products} onChange={(e) => set("products", e.target.value)} className={`${inputCls} font-mono text-xs`} /></L>
        </div>
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancelar</Button>
          <Button data-testid="save-fighter-button" onClick={save} className="rounded-none bg-vorna-accent hover:bg-vorna-accentHover">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
